import { I18N } from 'aurelia-i18n';
import { NavigationInstruction, Router } from 'aurelia-router';
import numeral from 'numeral';
import { anything, instance, mock, resetCalls, when } from 'ts-mockito';
import { Business } from '../../models/business';
import { Account, Customer } from '../../models/customer';
import { FeeCalculationType } from '../../models/feeCalculationType';
import { ServiceResponse } from '../../models/service-response';
import { State } from '../../services/global-state';
import { HpfPaymentMethodService } from '../../services/hpf-payment-method.service';
import { PayrazrService } from '../../services/payrazr';
import { Model, PaymentFormCustomElement } from './payment-form';
import { EventAggregator } from 'aurelia-event-aggregator';
import { Portfolio } from '../../models/portfolio';
import { PaymentPlanType } from '../../models/paymentPlanType';
import { CheckFormData } from './check-form/check-form';

describe('NewPortalWeb', () => {
	describe('components', () => {
		describe('paymentForm', () => {
			let component: PaymentFormCustomElement;
			let cleanModel: Model;
			let stubState: State;
			const mockRouter: Router = mock(Router);
			const mockState: State = mock(State);
			const mockPayrazrService: PayrazrService = mock(PayrazrService);
			const mockElement: Element = mock(Element);
			const mockI18N: I18N = mock(I18N);
			const mockCustomer: Customer = mock(Customer);
			const mockBusiness: Business = mock(Business);
			const mockHpfPaymentMethodService: HpfPaymentMethodService = mock(HpfPaymentMethodService);
			const mockEventAggregator: EventAggregator = mock(EventAggregator);

			let accounts: Account[] = [];
			let firstAccount = new Account({});
			firstAccount.portfolioName = 'TestPortfolio';
			accounts.push(firstAccount);

			let portalSettings = {
				showResetButton: true,
				resetButtonLabel: 'ResetLabelTest',
				requireMinPayment: true,
				minimumPaymentAmount: 500,
				maximumPaymentAmount: 2500,
				fields: {
					cvv: {
						required: true,
						label: 'testcvv'
					},
					cardCustom: [],
					checkCustom: [],
					billingAddress: {
						label: ''
					},
					billingCity: {
						label: ''
					},
					billingState: {
						label: ''
					},
					billingPhone: {
						label: ''
					},
					billingEmail: {
						label: ''
					},
					transactionDescription: {
						label: ''
					},
					bankAccountNumberVerification: {
						visible: false,
						required: false
					},
					checkNumber: {
						visible: false,
						required: false
					}
				},
				businessSettings: {
					enableApplePay: true
				},
				paymentMethods: {
					allowApplePay: true,
					allowAch: false
				},
				setDefaultPaymentMethodACH: false
			};

			beforeEach(() => {
				const i18next = {
					t: (a, b) => a
				};
				when(mockI18N.i18next).thenReturn(i18next);
				spyOn(i18next, 't').and.returnValue('localized');
			});

			beforeEach(() => {
				when(mockState.business).thenReturn(instance(mockBusiness));
				when(mockBusiness.portalSettings).thenReturn(portalSettings);
				when(mockRouter.currentInstruction).thenReturn({
					config: {
						name: 'ezpay',
					}
				} as NavigationInstruction);

				when(mockPayrazrService.getCustomerPaymentMethodsByCustomerId()).thenResolve({
					isSuccessful: false
				});

				stubState = new State(
					instance(mockI18N),
					instance(mockEventAggregator)
				);

				stubState.business = instance(mockBusiness);
				stubState.business.addCustomerPortfolios = () => { };

				spyOn(stubState.business, 'addCustomerPortfolios')
					.and
					.callFake(() => { });

				cleanModel = new Model(
					instance(mockState),
					instance(mockPayrazrService),
					instance(mockI18N)
				);

				when(mockHpfPaymentMethodService
					.verifyCheckAccepted(
						mockCustomer.customerKey,
						cleanModel.paymentMethod,
						'C'))
					.thenResolve({
						isSuccessful: true
					} as ServiceResponse);

				component = new PaymentFormCustomElement(
					instance(mockElement),
					stubState,
					instance(mockPayrazrService),
					instance(mockRouter),
					instance(mockI18N),
					instance(mockHpfPaymentMethodService),
					instance(mockEventAggregator)
				);

				component.model = JSON.parse(JSON.stringify(cleanModel));

				// Injected @bindable properties
				component.accounts = accounts;

				// TODO: Setup other bindable properties and required data such as payment methods
			});

			describe('initalize', () => {
				it('should initialize component', () => {
					expect(component).toBeTruthy();

					// Initialized properties in the constructor
					expect(component.showResetButton).toEqual(portalSettings.showResetButton);
					expect(component.resetButtonLabel).toEqual(portalSettings.resetButtonLabel);
					expect(component.enforceMinPayment).toEqual(portalSettings.requireMinPayment);
					expect(component.disableField).toEqual(false);
					expect(component.showConvenienceFees).toEqual(false);
					expect(component.minimumPaymentAmount).toEqual(numeral(portalSettings.minimumPaymentAmount).format('$0,0.00'));
					expect(component.maximumPaymentAmount).toEqual(numeral(portalSettings.maximumPaymentAmount).format('$0,0.00'));
				});
			});

			describe('attached()', () => {
				// TODO: Write this test - it relies heavily on other functions being called to setup data. 
				when(mockBusiness.fetchPortfolioSettings(anything())).thenReturn({

				});

				it('should return with no payment methods', () => {
					spyOn(component, 'showAcceptedPaymentMethods').and.stub();
					// component.attached();
					// expect(component.model.minPayment).toEqual(portalSettings.minimumPaymentAmount || 0.01);
				});

			});

			describe('applyQueryStringParams()', () => {

				let params = {
					locked: true,
					paymentdescription: 'description',
					paymentamount: '500',
					billingaddress: '123 test',
					customername: 'testy mctesterson',
					zipcode: '85388',
					billingcity: 'test',
					billingstate: 'AZ',
				};

				let UMparams = {
					umtransamt: '400',
					umdescription: 'um description',
					umname: 'um testy',
					umrouting: '123456789',
					umphonenumber: '1234567890',
					umemail: 'testy@test.test',
					umstreet: '123 test',
					umzip: '85388',
				};

				it('should not set values with empty params', async () => {
					when(mockPayrazrService.getParameterValue(anything())).thenCall((key: string) => {
						key = key.toLowerCase();
						const value = params[key];
						return value || '';
					});
					spyOn(component, 'calculateTotalAmount').and.stub();
					component.model = cleanModel;

					component.applyQueryStringParams();
					expect(component.model).toEqual(cleanModel);
				});

				it('should set values with params', async () => {
					when(mockPayrazrService.getParameterValue(anything())).thenCall((key: string) => {
						key = key.toLowerCase();
						const value = params[key];
						return value || '';
					});
					spyOn(component, 'calculateTotalAmount').and.stub();
					component.model = cleanModel;

					component.applyQueryStringParams();
					// expect(component.disableField).toBeTruthy();
					expect(component.model.description).toEqual(params.paymentdescription.substr(0, 80));
					expect(component.model.amount).toEqual(params.paymentamount.substr(0, 12));
					expect(component.model.billingAddress).toEqual(params.billingaddress.substr(0, 50));
					expect(component.model.accountName).toEqual(params.customername.substr(0, 100));
					expect(component.model.accountZip).toEqual(params.zipcode.substr(0, 9));
					expect(component.model.billingCity).toEqual(params.billingcity.substr(0, 25));
					expect(component.model.billingState).toEqual(params.billingstate.substr(0, 5));

					expect(component.calculateTotalAmount).toHaveBeenCalled();
				});

				it('should set values with UM params', async () => {
					when(mockPayrazrService.getParameterValue(anything())).thenCall((key: string) => {
						key = key.toLowerCase();
						const value = UMparams[key];
						return value || '';
					});
					spyOn(component, 'calculateTotalAmount').and.stub();
					component.model = cleanModel;

					component.applyQueryStringParams();
					expect(component.model.amount).toEqual(UMparams.umtransamt.substr(0, 12));
					expect(component.model.description).toEqual(UMparams.umdescription.substr(0, 80));
					expect(component.model.accountName).toEqual(UMparams.umname.substr(0, 100));
					expect(component.model.checkData.transitNumber).toEqual(UMparams.umrouting.substr(0, 100));
					expect(component.model.billingPhone).toEqual(UMparams.umphonenumber.substr(0, 100));
					expect(component.model.billingEmail).toEqual(UMparams.umemail.substr(0, 100));
					expect(component.model.accountZip).toEqual(UMparams.umzip.substr(0, 9));

					expect(component.calculateTotalAmount).toHaveBeenCalled();
				});
			});

			xdescribe('getFeeInformation()', () => {

				beforeEach(() => {
					component.model = JSON.parse(JSON.stringify(cleanModel));
					resetCalls(mockPayrazrService);
				});

				const fixedConvenienceFee = numeral(1.0).format('0.00');
				const percentageConvenienceFee = numeral(20.0).format('0.00');
				const feeInformationResponses =
				{
					fixed: {
						isSuccessful: true,
						entity: {
							fee: 1.0,
							feeValue: numeral(1.0).format('0.00'),
							feeCalculationType: FeeCalculationType.FixedAmount
						}
					},
					percentage: {
						isSuccessful: true,
						entity: {
							fee: 20.0,
							feeValue: numeral(20.0).format('0.0000'),
							feeCalculationType: FeeCalculationType.Percentage
						}
					}
				};
				const testCaseEscenarios = [
					{
						description: 'should get fee information for payment method card with feeCalculation type as FeeCalculationType.FixedAmount',
						feeInformationResponse: feeInformationResponses.fixed,
						input: {
							convenienceFee: fixedConvenienceFee,
							paymentMethod: {
								isCard: true
							}
						}
					},
					{
						description: 'should get fee information for payment method card with feeCalculation type as FeeCalculationType.Percentage',
						feeInformationResponse: feeInformationResponses.percentage,
						input: {
							convenienceFee: percentageConvenienceFee,
							paymentMethod: {
								isCard: true
							}
						}
					},
					{
						description: 'should get fee information for payment method card from wallet with feeCalculation type as FeeCalculationType.FixedAmount',
						feeInformationResponse: feeInformationResponses.fixed,
						input: {
							convenienceFee: fixedConvenienceFee,
							paymentMethod: {
								isCard: false,
								isWallet: true,
								vm: {
									getSelectedPaymentMethod: {
										cardInfoKey: 1
									}
								}
							}
						}
					},
					{
						description: 'should get fee information for payment method card from wallet with feeCalculation type as FeeCalculationType.Percentage',
						feeInformationResponse: feeInformationResponses.percentage,
						input: {
							convenienceFee: percentageConvenienceFee,
							paymentMethod: {
								isCard: false,
								isWallet: true,
								vm: {
									getSelectedPaymentMethod: {
										cardInfoKey: 1
									}
								}
							}
						}
					},
					{
						description: 'should get fee information for payment method ACH from wallet with feeCalculation type as FeeCalculationType.FixedAmount',
						feeInformationResponse: feeInformationResponses.fixed,
						input: {
							convenienceFee: fixedConvenienceFee,
							paymentMethod: {
								isCard: false,
								isWallet: true,
								vm: {
									getSelectedPaymentMethod: {
										checkInfoKey: 1
									}
								}
							}
						}
					},
					{
						description: 'should get fee information for payment method ACH from wallet with feeCalculation type as FeeCalculationType.Percentage',
						feeInformationResponse: feeInformationResponses.percentage,
						input: {
							convenienceFee: percentageConvenienceFee,
							paymentMethod: {
								isCard: false,
								isWallet: true,
								vm: {
									getSelectedPaymentMethod: {
										checkInfoKey: 1
									}
								}
							}
						}
					}
				];

				testCaseEscenarios.forEach((scenario) => {
					it(scenario.description, async () => {

						// arrenge
						component.model.paymentMethod = scenario.input.paymentMethod;

						const convenienceFee = numeral(scenario.feeInformationResponse.entity.fee).format('0.00');
						const totalAmount = component.model.amount + scenario.feeInformationResponse.entity.fee;

						if (scenario.feeInformationResponse.entity.feeCalculationType === FeeCalculationType.Percentage) {
							scenario.feeInformationResponse.entity.feeValue = numeral(scenario.feeInformationResponse.entity.fee).format('0.0000');
						} else {
							scenario.feeInformationResponse.entity.feeValue = numeral(scenario.feeInformationResponse.entity.fee).format('0.00');
						}

						when(mockPayrazrService.calculateFee(anything(), anything(), anything(), anything(), anything(), anything(), anything(), anything(), anything())).thenResolve(scenario.feeInformationResponse);

						spyOn(component, 'calculateTotalAmount').and.callThrough();

						// act
						await component.getFeeInformation();
						component.calculateTotalAmount();

						// assert
						expect(component.fee).toBe(scenario.feeInformationResponse.entity.fee);
						expect(component.model.convenienceFee).toBe(convenienceFee);
						expect(component.feeCalculationType).toBe(scenario.feeInformationResponse.entity.feeCalculationType);
						expect(component.feeValue).toBe(scenario.feeInformationResponse.entity.feeValue);
						expect(component.totalAmount).toBe(totalAmount);
						if (scenario.input.paymentMethod.isCard) {
							expect(component.cardInfoKey).toBeNull();
							expect(component.cardNumber).not.toBeNull();
						} else if (scenario.input.paymentMethod.isWallet) {
							if (scenario.input.paymentMethod.vm.getSelectedPaymentMethod.cardInfoKey) {
								expect(component.cardInfoKey).not.toBeNull();
							} else if (scenario.input.paymentMethod.vm.getSelectedPaymentMethod.checkInfoKey) {
								expect(component.cardInfoKey).toBeNull();
								expect(component.cardNumber).toBeNull();
							}
						}
						expect(component.calculateTotalAmount).toHaveBeenCalled();
					});
				});

				it('should handle fail', () => {
					when(mockPayrazrService.calculateFee(anything(), anything(), anything(), anything(), anything(), anything(), anything(), anything(), anything())).thenReject(new Error());
					const spyGetFeeInformation = spyOn(component, 'getFeeInformation').and.stub();

					component.getFeeInformation();
					expect(spyGetFeeInformation).toHaveBeenCalled();
				});

			});

			describe('showAcceptedPaymentMethods', () => {
				it('should add call getCustomerPaymentMethods() when Apple Pay enabled', () => {
					// Arrange
					const mockPortfolio = mock(Portfolio);
					mockPortfolio.paymentMethods = {
						acceptsCredit: true,
						acceptsDebit: true
					};

					component.globalState.features.enableApplePay = true;
					component.paymentPlanType = PaymentPlanType.NotSet;
					component.paymentMethods = null;

					spyOn(component, 'getCustomerPaymentMethods')
						.and
						.returnValue(Promise.resolve());

					// Act
					component.showAcceptedPaymentMethods(mockPortfolio);

					// Assert
					expect(component.getCustomerPaymentMethods)
						.toHaveBeenCalledTimes(1);
				});
			});

			describe('setDefaultPaymentMethod', () => {

				beforeEach(() => {
					component.paymentMethods = [];
					component.globalState.business = instance(mockBusiness);
					component.globalState.business.portalSettings.paymentMethods.allowAch = false;
					component.globalState.business.portalSettings.setDefaultPaymentMethodACH = false;
				});

				it('should set Card as default payment method when no payment methods exists', () => {

					// arrange
					component.globalState.business.portalSettings.paymentMethods.allowAch = false;

					// act
					component.setDefaultPaymentMethod('cardLabel');

					// assert
					expect(component.model.paymentMethod.name).toBe('cardLabel');
					expect(component.model.paymentMethod.isCard).toBeTrue();
					expect(component.paymentMethods.length).toBe(1);

				});

				it('should set eCheck as default payment method when ACH is available and Portal Settings allow it', () => {

					// arrange
					component.paymentMethods.push({ isCheck: true, name: 'eCheck' });
					component.globalState.business.portalSettings.paymentMethods.allowAch = true;
					component.globalState.business.portalSettings.setDefaultPaymentMethodACH = true;

					// act
					component.setDefaultPaymentMethod('cardLabel');

					// assert
					expect(component.model.paymentMethod.name).toBe('eCheck');
					expect(component.model.paymentMethod.isCheck).toBeTrue();

				});
			});

			describe('getSubmitEventData()',() => {

				const i18next = {
					t: (a, b) => a
				};

				beforeEach(() => {
					when(mockI18N.i18next).thenReturn(i18next);
				});

				it('should include error for non matching bank account number when payment method is ACH and verification is required', async () => {

					component.globalState.business = instance(mockBusiness);
					component.globalState.business.portalSettings.fields.bankAccountNumberVerification.visible = true;
					component.globalState.business.portalSettings.fields.bankAccountNumberVerification.required = true;

					spyOn(i18next, 't')
						.withArgs('nameRequired', 'Name is required').and.returnValue('Name is required')
						.withArgs('enterBothFirstAndLastName', 'Please enter both a first and last name').and.returnValue('Please enter both a first and last name')
						.withArgs('zipCodeIsRequired', 'Zip code is required').and.returnValue('Zip code is required')
						.withArgs('transitNumberRequired', 'Transit number is required').and.returnValue('Transit number is required')
						.withArgs('bankAccountNumbersDoNotMatch', 'Bank Account Numbers do not match').and.returnValue('Bank Account Numbers do not match')
						;

					component.maxIsBalanceDue = false;
					component.model = cleanModel;
					component.model.amount = 1;
					component.model.accountName = "Felipe Garcia";
					component.model.minPayment = 1;
					component.model.maxPayment = 9999;
					component.model.billingPhoneSettings.visible = false;
					component.model.billingEmailSettings.visible = false;
					component.model.descriptionSettings.visible = false;
					let checkFormData = new CheckFormData(instance(mockI18N), component.globalState);
					checkFormData.accountNumber = '12345678';
					checkFormData.accountNumberVerification = '1234567';
					checkFormData.customFields = [];
					component.model.paymentMethod = { name: 'eCheck', vmName: './check-form/check-form', vm: checkFormData, isCheck: true };
					component.merchantKey = 1;

					let result = await component.getSubmitEventData();

					expect(result.errors).toBeTruthy();
					expect(result.errors.length).toBeGreaterThan(0);
					expect(result.errors.filter(x => x === 'Bank Account Numbers do not match').length).toBe(1);

				});

				it('should include error for invalid email format input ', async () => {

					component.globalState.business = instance(mockBusiness);
					component.globalState.business.portalSettings.fields.bankAccountNumberVerification.visible = true;
					component.globalState.business.portalSettings.fields.bankAccountNumberVerification.required = true;

					spyOn(i18next, 't')
						.withArgs('nameRequired', 'Name is required').and.returnValue('Name is required')
						.withArgs('enterBothFirstAndLastName', 'Please enter both a first and last name').and.returnValue('Please enter both a first and last name')
						.withArgs('zipCodeIsRequired', 'Zip code is required').and.returnValue('Zip code is required')
						.withArgs('transitNumberRequired', 'Transit number is required').and.returnValue('Transit number is required')
						.withArgs('bankAccountNumbersDoNotMatch', 'Bank Account Numbers do not match').and.returnValue('Bank Account Numbers do not match')
						.withArgs('thisEmailAddressIsNotValid', 'This Email address is not valid.').and.returnValue('This Email address is not valid.')
						;

					component.maxIsBalanceDue = false;
					component.model = cleanModel;
					component.model.amount = 1;
					component.model.accountName = "test account";
					component.model.minPayment = 1;
					component.model.maxPayment = 9999;
					component.model.billingPhoneSettings.visible = false;
					component.model.billingEmailSettings.visible = true;
					component.model.billingEmail = "yzhang123@ gmail.com";
					component.model.descriptionSettings.visible = false;
					let checkFormData = new CheckFormData(instance(mockI18N), component.globalState);
					checkFormData.accountNumber = '12345678';
					checkFormData.accountNumberVerification = '12345678';
					checkFormData.customFields = [];
					component.model.paymentMethod = { name: 'eCheck', vmName: './check-form/check-form', vm: checkFormData, isCheck: true };
					component.merchantKey = 1;

					let result = await component.getSubmitEventData();

					expect(result.errors).toBeTruthy();
					expect(result.errors.length).toBeGreaterThan(0);
					expect(result.errors.filter(x => x === 'This Email address is not valid.').length).toBe(1);

				});
			});
		});
	});
});
